"use client";

import React, { useState } from "react";
import {
  MapPin,
  Phone,
  Mail,
  Send,
  Facebook,
  Instagram,
  Youtube,
  Linkedin,
  Twitter,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import PageHeader from "../Header";
import SectionHeading from "../navbar/SectionHeading";
import { cn } from "../ui/utils";
import { apiFetch } from "../../services/api/Common";

/* ================= TYPES ================= */
interface ContactFormData {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

interface ContactInfo {
  icon: React.ComponentType<any>;
  label: string;
  value: string;
  link: string;
  color: string;
}

/* ================= COMPONENT ================= */
const ContactUs = () => {
  const [formData, setFormData] = useState<ContactFormData>({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const [errors, setErrors] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<
    "idle" | "success" | "error"
  >("idle");

  /* ================= CONTACT INFO ================= */
  const contactInfo: ContactInfo[] = [
    {
      icon: MapPin,
      label: "Office Address",
      value: "Olympic Bhawan, Sector 3, Panchkula, Haryana 134115",
      link: "https://maps.google.com/?q=Olympic+Bhawan+Sector+3+Panchkula+Haryana+134115",
      color: "bg-blue-100 text-blue-600",
    },
    {
      icon: Phone,
      label: "Phone Number",
      value: "0172 258 6803",
      link: "tel:01722586803",
      color: "bg-yellow-100 text-yellow-600",
    },
    {
      icon: Mail,
      label: "Email Address",
      value: "hrolympicassociation@gmail.com",
      link: "mailto:hrolympicassociation@gmail.com",
      color: "bg-blue-100 text-blue-600",
    },
  ];

  /* ================= SOCIAL MEDIA ================= */
  const socialLinks = [
    {
      icon: Facebook,
      label: "Facebook",
      url: "https://www.facebook.com/p/Haryana-Olympic-Association-61574667794025/",
      color: "hover:text-blue-700 text-blue-600",
    },
    {
      icon: Instagram,
      label: "Instagram",
      url: "https://www.instagram.com/haryanaolympicassociation",
      color: "hover:text-pink-700 text-pink-600",
    },
    {
      icon: Youtube,
      label: "YouTube",
      url: "https://www.youtube.com/@HaryanaOlympicAssociation",
      color: "hover:text-red-700 text-red-600",
    },
    {
      icon: Linkedin,
      label: "LinkedIn",
      url: "https://in.linkedin.com/company/haryanaolympicassociation",
      color: "hover:text-blue-700 text-blue-700",
    },
    {
      icon: Twitter,
      label: "Twitter",
      url: "https://x.com/Haryana_olympic",
      color: "hover:text-blue-700 text-blue-400",
    },
  ];

  /* ================= FORM HANDLERS ================= */
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const newErrors = {
      name: "",
      email: "",
      subject: "",
      message: "",
    };

    let hasError = false;

    // ================= NAME =================
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
      hasError = true;
    }

    // ================= EMAIL =================
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
      hasError = true;
    } else if (
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)
    ) {
      newErrors.email = "Invalid email address";
      hasError = true;
    }

    // ================= SUBJECT =================
    if (!formData.subject.trim()) {
      newErrors.subject = "Subject is required";
      hasError = true;
    }

    // ================= MESSAGE =================
    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
      hasError = true;
    }

    setErrors(newErrors);

    if (hasError) return;

    setIsSubmitting(true);

    try {
      // ================= API CALL =================
      const response = await apiFetch("contacts", {
        method: "POST",
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          subject: formData.subject,
          message: formData.message,
        }),
      });

      console.log("API Response:", response);

      // ================= SUCCESS =================
      if (response?.data?.success === true && response?.data?.status === 200) {
        setSubmitStatus("success");

        // ================= RESET FORM =================
        setFormData({
          name: "",
          email: "",
          phone: "",
          subject: "",
          message: "",
        });

        setErrors({
          name: "",
          email: "",
          subject: "",
          message: "",
        });
      } else {
        setSubmitStatus("error");
      }
    } catch (error) {
      console.error("Error submitting form:", error);

      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);

      setTimeout(() => {
        setSubmitStatus("idle");
      }, 5000);
    }
  };

  /* ================= RENDER ================= */
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
      {/* ================= HERO SECTION ================= */}

      <PageHeader
        title="Contact Us"
        paragraph="Get in touch with the House of Athletes"
        backgroundImage=""
        align="center"
      />

      {/* ================= CONTACT + FORM SECTION ================= */}
      <div className="bg-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* ================= LEFT SIDE CONTENT ================= */}
            <div>
              {/* Section Header */}
              <SectionHeading
                title={
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                    Get In Touch
                  </h2>
                }
                subtitle={
                  <div className="mb-3">
                    We'd love to hear from you. Reach out through any of the
                    contact methods below or send us a message using the form.
                  </div>
                }
              />

              {/* Contact Cards */}
              <div className="space-y-5 mt-5">
                {contactInfo.map((info, index) => {
                  const Icon = info.icon;

                  return (
                    <a
                      key={index}
                      href={info.link}
                      target={
                        info.label === "Office Address" ? "_blank" : "_self"
                      }
                      rel="noopener noreferrer"
                      className="block group bg-gray-50 hover:bg-white border border-gray-100 rounded-2xl p-2 shadow-sm hover:shadow-lg transition-all duration-300 cursor-pointer"
                    >
                      <div className="flex items-start gap-4">
                        <div
                          className={`${info.color} w-12 h-12 rounded-full flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform`}
                        >
                          <Icon size={22} />
                        </div>

                        <div>
                          <h3 className="font-semibold text-gray-900 text-lg mb-1">
                            {info.label}
                          </h3>

                          <p className="text-gray-600 text-sm leading-relaxed break-all">
                            {info.value}
                          </p>
                        </div>
                      </div>
                    </a>
                  );
                })}
              </div>

              {/* Social Media */}
              <div className="mt-10 flex items-center gap-5">
                <h3 className="text-2xl font-bold text-gray-900">Follow Us</h3>

                <div className="flex items-center gap-4 flex-wrap">
                  {socialLinks.map((social, index) => {
                    const Icon = social.icon;

                    return (
                      <a
                        key={index}
                        href={social.url}
                        aria-label={social.label}
                        title={social.label}
                        className={`bg-white p-4 rounded-full shadow-md hover:shadow-xl transition-all duration-300 transform hover:scale-110 ${social.color}`}
                      >
                        <Icon size={28} />
                      </a>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* ================= RIGHT SIDE FORM ================= */}
            <div className="bg-gray-50 rounded-3xl p-6 md:p-8 shadow-lg border border-gray-100">
              <SectionHeading
                title={
                  <h4 className="font-bold text-2xl">Send Us a Message</h4>
                }
                subtitle={
                  <div className="mb-3">
                    Fill out the form and we'll contact you shortly.
                  </div>
                }
              />

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name + Email */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Name */}
                  <div className="space-y-2">
                    <label
                      htmlFor="name"
                      className="flex items-center gap-1 font-semibold text-gray-700"
                    >
                      Your Name <span className="text-red-600">*</span>
                    </label>

                    <Input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="Enter your full name"
                      value={formData.name}
                      onChange={handleInputChange}
                      aria-invalid={!formData.name}
                      className="rounded-lg border-2 border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition"
                    />
                    {errors.name && (
                      <p className="text-sm text-red-600">{errors.name}</p>
                    )}
                  </div>

                  {/* Email */}
                  <div className="space-y-2">
                    <label
                      htmlFor="email"
                      className="flex items-center gap-1 font-semibold text-gray-700"
                    >
                      Your Email <span className="text-red-600">*</span>
                    </label>

                    <Input
                      id="email"
                      name="email"
                      type="text"
                      placeholder="Enter your email"
                      value={formData.email}
                      onChange={handleInputChange}
                      aria-invalid={!formData.email}
                      className="rounded-lg border-2 border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition"
                    />
                    {errors.email && (
                      <p className="text-sm text-red-600">{errors.email}</p>
                    )}
                  </div>
                </div>

                {/* Phone + Subject */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Phone */}
                  <div className="space-y-2">
                    <label
                      htmlFor="phone"
                      className="font-semibold text-gray-700"
                    >
                      Phone Number
                    </label>

                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="Enter phone number"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="rounded-lg border-2 border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition"
                    />
                  </div>

                  {/* Subject */}
                  <div className="space-y-2">
                    <label
                      htmlFor="subject"
                      className="flex items-center gap-1 font-semibold text-gray-700"
                    >
                      Subject <span className="text-red-600">*</span>
                    </label>

                    <Input
                      id="subject"
                      name="subject"
                      type="text"
                      placeholder="Enter subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      aria-invalid={!formData.subject}
                      className="rounded-lg border-2 border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition"
                    />
                    {errors.subject && (
                      <p className="text-sm text-red-600">{errors.subject}</p>
                    )}
                  </div>
                </div>

                {/* Message */}
                <div className="space-y-2">
                  <label
                    htmlFor="message"
                    className="flex items-center gap-1 font-semibold text-gray-700"
                  >
                    Message <span className="text-red-600">*</span>
                  </label>

                  <textarea
                    id="message"
                    name="message"
                    rows={6}
                    placeholder="Enter your message..."
                    value={formData.message}
                    onChange={handleInputChange}
                    aria-invalid={!formData.message}
                    className={cn(
                      "w-full px-4 py-3 rounded-lg border-2 border-gray-200 transition resize-none outline-none",
                      "focus:border-blue-500 focus:ring-2 focus:ring-blue-200",
                      "aria-invalid:border-red-500 aria-invalid:ring-red-200",
                    )}
                  />
                  {errors.message && (
                    <p className="text-sm text-red-600">{errors.message}</p>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold py-6 px-6 rounded-lg transition-all duration-300 flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={20} />
                      Send Message
                    </>
                  )}
                </Button>

                {/* ================= STATUS MESSAGE ================= */}
                {submitStatus === "success" && (
                  <div className="mt-4 flex items-center gap-2 text-green-600 font-medium">
                    <CheckCircle size={20} />
                    Contact request submitted successfully.
                  </div>
                )}

                {submitStatus === "error" && (
                  <div className="mt-4 flex items-center gap-2 text-red-600 font-medium">
                    <AlertCircle size={20} />
                    Something went wrong. Please try again.
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* ================= GOOGLE MAP SECTION ================= */}
      <div className="w-full bg-gray-100 py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4">
          {/* Map Container */}
          <div className="w-full h-[300px] md:h-[500px] rounded-2xl overflow-hidden shadow-xl">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d4080.3089574377395!2d76.86452017995742!3d30.686979089682037!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390f937e9bbf3319%3A0xeaef2a92699ff396!2sHaryana%20Olympic%20Association!5e0!3m2!1sen!2sin!4v1779685764923!5m2!1sen!2sin"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen={true}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;
